import { Component, EventEmitter, Inject, Input, OnChanges, Optional, Output, SimpleChanges } from '@angular/core';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';

@GridModalComponent('chargetest')
@Component({
    selector: 'tt-charge-test',
    templateUrl: './charge-test.component.html',
    styleUrls: ['./charge-test.component.css'],
})
export class ChargeTestComponent implements OnChanges {
    @Input()
    public params?: ChargeTestParams;

    public get?: ChargeTestGet;

    @Output()
    public submit = new EventEmitter<ErrorMessageResponse>();

    public saving = false;

    constructor(
        private datatask: DataTaskService,
        private modal: ModalService,
        @Optional() @Inject(TT_STATE_DIALOG) stateDialog: StateDialogContext,
    ) {
        this.initFromDialog(stateDialog);
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;
            const { errorcode, errormessage } = await this.saveChargeTest(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ errorcode, errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getChargeTest(this.params);
        }
    }

    /**
     * Retrieves the chargetest with the given keyno.
     *
     * @datatask 3921
     * @datataskName sp_ws_p2_chargetest_get
     * @param params keyno for the chargetest to get.
     * @returns promise containing the charge.
     */
    private async getChargeTest(params: ChargeTestParams): Promise<ChargeTestGet> {
        return (await this.datatask.Post(3921, params))[0];
    }

    /**
     * Saves the given charge test.
     *
     * @datatask 3922
     * @datataskName sp_ws_p2_chargetest_save
     * @param params the charge test to save.
     * @returns promise when the save fullfils containig error message.
     */
    private async saveChargeTest(params: ChargeTestGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3922, params))[0];
    }

    private async init() {
        await this.loadGet();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    private async initFromDialog(state?: StateDialogContext) {
        if (!!state) {
            state.setSize({ width: 'min(100rem, 95vw)' });
            this.params = state.params as unknown as ChargeTestParams;
            await this.init();
            state.setHeading(this.get?.webpage_heading ?? '');
            this.submit.subscribe({ next: (value: ErrorMessageResponse) => state.close(value) });
        }
    }
}

export interface ChargeTestParams {
    charge_keyno: string;
    chargetest_keyno: string;
}

export interface ChargeTestGet {
    webpage_heading: string;
    charge_keyno: string;
    chargetest_keyno: string;
    charge_id: string;
    test_no: string;
    testcode: string;
    yield_point: string;
    tensile_strength: string;
    elongation: string;
    contraction: string;
    impact: string;
    temperature_c: string;
    dimension: string;
    note: string;
    is_hidden: '0' | '1';
    reg_by_portal_user_name: string;
    reg_datetime: string;
    mod_by_portal_user_name: string;
    mod_datetime: string;
}
